import { 
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ViewChild,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  OnDestroy, 
  ElementRef
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Observable, Subject, combineLatest } from 'rxjs';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ViewAudioComponent } from '../view-audio/view-audio.component';
import { MediaModel } from 'src/app/story/models/media.model';
import { ViewState } from '../../state/view.state';
import { ViewUIModel } from '../../models/view-ui.model';
import { ViewUI } from '../../models/view-ui.enum';
import { ViewUIService } from '../../view-ui.service';
import { filter, take, takeUntil } from 'rxjs/operators';
import { TextUtils } from 'src/app/shared/libraries/text-utils/text-utils';
import { PopupRef } from 'src/app/shared/features/popup/popup.ref';
import { ViewChaptersComponent } from '../view-chapters/view-chapters.component';
import { PopupService } from 'src/app/shared/features/popup/popup.service';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { StoryState } from 'src/app/story/state/story.state';
import { StoryModel } from 'src/app/story/models/story.model';

@Component({
  selector: 'app-view-title',
  templateUrl: './view-title.component.html',
  styleUrls: ['./view-title.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    PopupService
  ]
})
export class ViewTitleComponent implements OnInit, OnDestroy {
  // ---------- Children ----------
  @ViewChild('audio')
  audio: ViewAudioComponent;

  @ViewChild('title', { static: false })
  title: ElementRef<HTMLElement>;



  // ---------- Observables ----------
  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  @Select(StoryChaptersState.getSelectedChapter)
  chapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getChapterCount)
  chapterCount$: Observable<number>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  chapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getSelectedChapterAudio)
  selectedChapterAudio$: Observable<MediaModel>;

  @Select(ViewState.getUI)
  layout$: Observable<ViewUIModel>;

  

  // ---------- Input / Output ----------
  @Input()
  size: 'medium' | 'large';
  
  @Input()
  alignTitle: 'center' | 'left';

  @Input()
  public get isPopulated() {
    return this._isPopulated;
  }
  public set isPopulated(value) {
    this._isPopulated = value;
    this.isPopulatedChange.emit(this.isPopulated);
  }
  private _isPopulated = false;

  @Output()
  isPopulatedChange = new EventEmitter<boolean>();

  @Input()
  public get menuEnabled() {
    return this._menuEnabled;
  }
  public set menuEnabled(value) {
    this._menuEnabled = value;
  }
  private _menuEnabled = true;


  
  // ---------- Class Variables ----------
  viewLayout = ViewUI;
  ngUnsubscribe = new Subject();
  storyTitle = '';
  chapterTitle = '';
  chapterAudio: MediaModel = null;
  chaptersPopup: PopupRef<ViewChaptersComponent>;

  
  // ---------- Lifecycle ----------
  constructor(
    private layoutService: ViewUIService,
    private changeDetectorRef: ChangeDetectorRef,
    private popupService: PopupService,
    private store: Store
  ) { }

  ngOnInit() {
    this.initContentListener();
    this.initLayoutListener();
    this.initChapterListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  isEnabled(layout: ViewUI) {
    return this.layoutService.isUIEnabled(layout);
  }
  
  
  
  // ---------- Listeners ----------
  initContentListener() {
    combineLatest(
      this.story$,
      this.chapter$, 
    ).pipe(
      filter(([story, chapter]) => chapter != null && story != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([story, chapter]) => {
      // Only populate bookmark in web app
      this.chapterTitle = TextUtils.normalize(chapter.info.displayName);
      

      this.storyTitle = (environment.application !== EnvironmentApplication.Viewer) ? TextUtils.normalize(story.displayName) : '';
      this.chapterTitle = TextUtils.normalize(chapter.info.displayName);

      this.isPopulated = !!this.chapterTitle || !!this.storyTitle;
    });
  }

  initLayoutListener() {
    this.layout$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((layout) => {
      this.changeDetectorRef.markForCheck();
    });
  }

  initChapterListener() {
    this.store.select(StoryChaptersState.getSelectedChapterIndex)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        if (this.chaptersPopup) {
          this.chaptersPopup.close();
        }
      });
  }


  // ---------- Interaction ----------
  handleAudioPlayClick() {
    if (this.audio.playing) {
      this.audio.pause();
    } else {
      this.audio.play();
    }
  }

  handleTitleClick() {
    if (!this.menuEnabled) {
      return;
    }

    if (this.chaptersPopup) {
      this.chaptersPopup.close();
    } else {
      this.chaptersPopup = this.popupService.open<ViewChaptersComponent>({
        origin: this.title.nativeElement,
        content: ViewChaptersComponent,
        width: '300px'
      });

      this.chaptersPopup.afterClosed$
        .pipe(
          takeUntil(this.ngUnsubscribe),
          take(1)
        )
        .subscribe((close) => {
          this.chaptersPopup = null;
        });
    }
  }

}
